import type { Localized } from '@/i18n/config'
import type { ServiceIcon, ServiceSlug } from '@/content/types'

export interface ServiceSummary {
    slug: ServiceSlug
    icon: ServiceIcon
    practice: boolean
    name: Localized<string>
    summary: Localized<string>
    highlights: Localized<Array<string>>
}

export const serviceSummaries: Array<ServiceSummary> = [
    {
        slug: 'tax',
        icon: 'landmark',
        practice: true,
        name: { en: 'Tax', sq: 'Tatime' },
        summary: {
            en: "Advisory, compliance and dispute support from a team that knows how Kosovo's tax administration works from the inside.",
            sq: 'Këshillim, përputhshmëri dhe mbështetje në kontestime nga një ekip që e njeh nga brenda mënyrën si punon administrata tatimore e Kosovës.'
        },
        highlights: {
            en: ['VAT advisory', 'Corporate and personal income tax', 'Tax audits and disputes', 'International taxation'],
            sq: ['Këshillim për TVSH', 'Tatimi në fitim dhe në të ardhura personale', 'Kontrolle dhe kontestime tatimore', 'Tatimet ndërkombëtare']
        }
    },
    {
        slug: 'accounting',
        icon: 'calculator',
        practice: true,
        name: { en: 'Accounting', sq: 'Kontabilitet' },
        summary: {
            en: 'Monthly accounting, IFRS financial statements and management reporting that close on time and read clearly.',
            sq: 'Kontabilitet mujor, pasqyra financiare sipas SNRF dhe raportim menaxherial që mbyllen në kohë dhe lexohen qartë.'
        },
        highlights: {
            en: ['Bookkeeping and monthly close', 'IFRS reporting', 'KPI dashboards', 'Budgeting and forecasting'],
            sq: ['Regjistrime dhe mbyllje mujore', 'Raportim sipas SNRF', 'Panele KPI', 'Buxhetim dhe parashikim']
        }
    },
    {
        slug: 'payroll-hr',
        icon: 'users',
        practice: true,
        name: { en: 'Payroll and HR', sq: 'Paga dhe burime njerëzore' },
        summary: {
            en: 'Accurate payroll, pension and tax withholding every month, with contracts and personnel files kept compliant.',
            sq: 'Paga, kontribute pensionale dhe tatim i mbajtur në burim pa gabime çdo muaj, me kontrata dhe dosje personeli në përputhje me ligjin.'
        },
        highlights: {
            en: ['Payroll administration', 'Employment contracts', 'HR compliance', 'Leave management'],
            sq: ['Administrimi i pagave', 'Kontratat e punës', 'Përputhshmëria në punësim', 'Menaxhimi i pushimeve']
        }
    },
    {
        slug: 'business-consulting',
        icon: 'compass',
        practice: true,
        name: { en: 'Business Consulting', sq: 'Konsulencë biznesi' },
        summary: {
            en: 'Strategy, process optimisation, internal controls and finance transformation for companies ready to scale.',
            sq: 'Strategji, optimizim procesesh, kontrolle të brendshme dhe transformim i financave për kompani gati për rritje.'
        },
        highlights: {
            en: ['Strategy and growth', 'Process optimisation', 'Internal controls', 'Restructuring'],
            sq: ['Strategji dhe rritje', 'Optimizim procesesh', 'Kontrolle të brendshme', 'Ristrukturim']
        }
    },
    {
        slug: 'ai-automation',
        icon: 'sparkles',
        practice: true,
        name: { en: 'AI Automation', sq: 'Automatizim me AI' },
        summary: {
            en: 'Invoice processing, document intelligence, copilots and reporting automation built around how finance teams really work.',
            sq: 'Përpunim faturash, inteligjencë dokumentesh, asistentë AI dhe automatizim raportesh të ndërtuara sipas punës reale të ekipeve financiare.'
        },
        highlights: {
            en: ['Invoice and OCR automation', 'AI copilots and assistants', 'Reporting automation', 'Document intelligence'],
            sq: ['Automatizim faturash dhe OCR', 'Kopilotë dhe asistentë AI', 'Automatizim raportesh', 'Inteligjencë dokumentesh']
        }
    },
    {
        slug: 'outsourcing',
        icon: 'globe',
        practice: true,
        name: { en: 'Outsourcing', sq: 'Outsourcing' },
        summary: {
            en: 'A virtual finance office in Kosovo: back office, administration, controller and CFO capacity on demand.',
            sq: 'Një zyrë financiare virtuale në Kosovë: back office, administratë, kontrollor dhe CFO sipas nevojës.'
        },
        highlights: {
            en: ['Offshore accounting', 'Back office and admin support', 'Controller as a Service', 'CFO as a Service'],
            sq: ['Kontabilitet offshore', 'Back office dhe mbështetje administrative', 'Kontrollor si shërbim', 'CFO si shërbim']
        }
    },
    {
        slug: 'cfo-services',
        icon: 'line-chart',
        practice: false,
        name: { en: 'CFO Services', sq: 'Shërbime CFO' },
        summary: {
            en: 'Senior finance leadership without a full-time hire: planning, investor reporting, cash and board support.',
            sq: 'Udhëheqje financiare e nivelit të lartë pa punësim të plotë: planifikim, raportim për investitorë, para të gatshme dhe mbështetje për bordin.'
        },
        highlights: {
            en: ['Fractional CFO', 'Cash and funding', 'Board and investor reporting', 'Finance team leadership'],
            sq: ['CFO me kohë të pjesshme', 'Para të gatshme dhe financim', 'Raportim për bordin dhe investitorët', 'Udhëheqje e ekipit financiar']
        }
    },
    {
        slug: 'offshore-accounting',
        icon: 'building',
        practice: false,
        name: { en: 'Offshore Accounting', sq: 'Kontabilitet offshore' },
        summary: {
            en: 'Qualified accountants in Kosovo working in your time zone, your software and your standards, at a fraction of local cost.',
            sq: 'Kontabilistë të kualifikuar në Kosovë që punojnë në zonën tuaj kohore, në softuerin dhe standardet tuaja, me një pjesë të kostos vendase.'
        },
        highlights: {
            en: ['UK, US and DACH clients', 'Xero, QuickBooks, DATEV', 'Month-end close', 'Dedicated team'],
            sq: ['Klientë në MB, SHBA dhe DACH', 'Xero, QuickBooks, DATEV', 'Mbyllje në fund të muajit', 'Ekip i dedikuar']
        }
    }
]

export const getServiceSummary = (slug: string): ServiceSummary | undefined => serviceSummaries.find(item => item.slug === slug)
